import { supabase } from './supabaseClient';

/**
 * Función para calcular días restantes
 */
const calcularDiasRestantes = (fechaFin) => {
  if (!fechaFin) return 0;
  const hoy = new Date();
  const fin = new Date(fechaFin);
  const diffTime = fin - hoy;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

/**
 * Función para calcular el estado basado en días restantes
 */
const actualizarEstado = (diasRestantes) => {
  if (diasRestantes <= 0) return 'vencido';
  if (diasRestantes <= 5) return 'por_vencer';
  return 'vigente';
};

/**
 * Verificar si un usuario está autorizado para acceder al sistema
 * @param {string} email - Email del usuario
 * @returns {Object} - Resultado de la verificación
 */
export const verificarUsuario = async (email) => {
  try {
    console.log('🔍 Verificando usuario:', email);

    // perfil_portal devuelve solo los datos que el portal necesita mostrar.
    // La tabla usuarios no es accesible desde el navegador.
    const { data, error } = await supabase.rpc('perfil_portal', { p_correo: email });

    if (error) {
      console.error('Error consultando usuario:', error);
      return {
        autorizado: false,
        mensaje: '❌ Error al verificar credenciales. Intenta nuevamente.',
        usuario: null
      };
    }

    if (!data) {
      return {
        autorizado: false,
        mensaje: '❌ Acceso denegado. Tu correo no está autorizado para acceder al sistema.',
        usuario: null
      };
    }

    const diasRestantes = calcularDiasRestantes(data.fecha_fin);
    const usuarioCompleto = {
      ...data,
      dias_restantes: diasRestantes,
      estado: actualizarEstado(diasRestantes)
    };

    if (diasRestantes <= 0) {
      return {
        autorizado: false,
        mensaje: `❌ Tu suscripción venció hace ${Math.abs(diasRestantes)} días. Contacta al administrador para renovar.`,
        usuario: usuarioCompleto
      };
    }

    return {
      autorizado: true,
      mensaje: '✅ Acceso autorizado',
      usuario: usuarioCompleto
    };

  } catch (error) {
    console.error('Error inesperado verificando usuario:', error);
    return {
      autorizado: false,
      mensaje: '❌ Error inesperado al verificar credenciales.',
      usuario: null
    };
  }
};

/**
 * Obtener información de un usuario específico
 * @param {string} email - Email del usuario
 * @returns {Object|null} - Datos del usuario o null si no existe
 */
export const obtenerUsuario = async (email) => {
  try {
    const { data, error } = await supabase.rpc('perfil_portal', { p_correo: email });

    if (error) throw error;
    if (!data) return null;

    const diasRestantes = calcularDiasRestantes(data.fecha_fin);

    return {
      ...data,
      dias_restantes: diasRestantes,
      estado: actualizarEstado(diasRestantes)
    };

  } catch (error) {
    console.error('Error obteniendo usuario:', error);
    throw new Error('Error al obtener información del usuario');
  }
};

/**
 * Actualizar última fecha de acceso del usuario
 * @param {string} email - Email del usuario
 * @returns {boolean} - true si se actualizó correctamente
 */
export const actualizarUltimoAcceso = async (email) => {
  try {
    // El login con credenciales ya lo marca dentro de login_portal.
    const { error } = await supabase.rpc('registrar_acceso_portal', { p_correo: email });

    if (error) {
      console.error('Error actualizando último acceso:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error inesperado actualizando último acceso:', error);
    return false;
  }
};

/**
 * Guardar el nombre y la empresa del propio usuario.
 * La función de la base solo puede tocar esas dos columnas; el correo no se
 * edita desde el portal porque es la llave de las filas del cliente en
 * seguimiento, calendario, procesos revisados y «Mi Empresa».
 * @param {string} email - Correo del usuario (identifica la fila)
 * @param {{nombre: string, empresa: string}} datos
 * @returns {Promise<{ok: boolean, mensaje: string, perfil: Object|null}>}
 */
export const guardarPerfilPropio = async (email, { nombre, empresa }) => {
  try {
    const { data, error } = await supabase.rpc('guardar_perfil_portal', {
      p_correo: email,
      p_nombre: nombre,
      p_empresa: empresa
    });

    if (error) {
      console.error('Error guardando perfil:', error);
      // PGRST202 = la función no existe todavía en la base. Pasa cuando el
      // frontend se despliega antes de ejecutar la migración, y el mensaje de
      // PostgREST viene en jerga de SQL: mejor decirlo en claro.
      if (error.code === 'PGRST202') {
        return { ok: false, mensaje: 'Esta opción aún no está activa: falta aplicar una migración en la base.', perfil: null };
      }
      // El resto de validaciones llegan como mensaje de Postgres y ya vienen
      // redactadas para el cliente.
      return { ok: false, mensaje: error.message || 'No se pudo guardar. Intenta nuevamente.', perfil: null };
    }
    if (!data) {
      return { ok: false, mensaje: 'No encontramos tu cuenta. Vuelve a iniciar sesión.', perfil: null };
    }

    return { ok: true, mensaje: 'Datos actualizados', perfil: data };
  } catch (error) {
    console.error('Error inesperado guardando perfil:', error);
    return { ok: false, mensaje: 'Error inesperado al guardar tus datos.', perfil: null };
  }
};

/**
 * Cambiar la contraseña del propio usuario.
 * Hay que demostrar la actual. Quien nunca cambió su clave entra con su RUC,
 * así que el RUC vale como contraseña actual (lo resuelve la función).
 * @param {string} email - Correo del usuario
 * @param {string} claveActual - Contraseña vigente, o el RUC
 * @param {string} claveNueva - Contraseña nueva (mínimo 8 caracteres)
 * @returns {Promise<{ok: boolean, mensaje: string}>}
 */
export const cambiarClavePropia = async (email, claveActual, claveNueva) => {
  try {
    const { data, error } = await supabase.rpc('cambiar_clave_portal', {
      p_correo: email,
      p_clave_actual: claveActual,
      p_clave_nueva: claveNueva
    });

    if (error) {
      console.error('Error cambiando contraseña:', error);
      return { ok: false, mensaje: error.message || 'No se pudo cambiar la contraseña.' };
    }
    // `false` es siempre lo mismo: la clave actual no coincide. La función no
    // distingue correo inexistente de clave incorrecta, para no enumerar cuentas.
    if (data !== true) {
      return { ok: false, mensaje: 'La contraseña actual no coincide. Si nunca la cambiaste, es tu RUC.' };
    }

    return { ok: true, mensaje: 'Contraseña actualizada' };
  } catch (error) {
    console.error('Error inesperado cambiando contraseña:', error);
    return { ok: false, mensaje: 'Error inesperado al cambiar la contraseña.' };
  }
};

/**
 * Obtener estadísticas rápidas del usuario
 * @param {string} email - Email del usuario
 * @returns {Object} - Estadísticas del usuario
 */
export const obtenerEstadisticasUsuario = async (email) => {
  try {
    const usuario = await obtenerUsuario(email);
    
    if (!usuario) {
      return null;
    }

    return {
      diasRestantes: usuario.dias_restantes,
      estado: usuario.estado,
      plan: usuario.plan,
      empresa: usuario.empresa,
      fechaVencimiento: usuario.fecha_fin,
      vigente: usuario.dias_restantes > 0
    };

  } catch (error) {
    console.error('Error obteniendo estadísticas:', error);
    return null;
  }
};

/**
 * Probar conexión con Supabase
 * @returns {boolean} - true si la conexión es exitosa
 */
export const probarConexion = async () => {
  try {
    const { error } = await supabase
      .from('seace_news')
      .select('*')
      .limit(1);

    if (error) {
      console.error('Error de conexión:', error);
      return false;
    }

    console.log('✅ Conexión a Supabase exitosa');
    return true;
  } catch (error) {
    console.error('Error probando conexión:', error);
    return false;
  }
};

// Exportar también las funciones auxiliares para uso interno
export { calcularDiasRestantes, actualizarEstado };